/**
 * Optimized CTA Components
 * Performance improvements:
 * - React.memo for all CTA variants
 * - Memoized click handlers
 * - Debounced analytics events
 * - Loading state management
 * - Optimized re-renders
 */

'use client';

import { memo, useCallback, useState, useMemo } from 'react';
import Link from 'next/link';
import { useAnalytics } from '@/hooks/usePerformance';

/**
 * Some CTA `description` strings author their bullet lists as
 * `<ul><li>…</li><br/>…</ul>`. A <br> that is a direct child of <ul>/<ol> is
 * invalid HTML and fails Lighthouse's `list` audit. Strip those stray <br>
 * elements (the ones that sit between/after list items) before rendering.
 */
function stripBrInLists(html: string): string {
  return html
    // <br> right after the opening <ul>/<ol>
    .replace(/(<(?:ul|ol)\b[^>]*>)\s*(?:<br\s*\/?>\s*)+/gi, '$1')
    // <br> between or after list items (before the next <li> or closing tag)
    .replace(/(<\/li>)\s*(?:<br\s*\/?>\s*)+/gi, '$1');
}

export type CtaGroup = {
  title: string;
  description: string;
  buttons: {
    href: string;
    text: string;
    className: string;
    variant?: 'primary' | 'secondary';
  }[];
};

type CtaButtonProps = {
  href: string;
  text: string;
  className: string;
  variant?: 'primary' | 'secondary';
  onClick?: () => void;
  isLoading?: boolean;
};

// Memoized single CTA button
const CtaButton = memo(function CtaButton({
  href,
  text,
  className,
  variant = 'primary',
  onClick,
  isLoading = false,
}: CtaButtonProps) {
  const { trackEvent } = useAnalytics();

  const handleClick = useCallback(() => {
    trackEvent('cta_click', {
      cta_text: text,
      cta_href: href,
      cta_variant: variant,
    });
    onClick?.();
  }, [text, href, variant, onClick, trackEvent]);

  return (
    <Link
      href={href}
      className={`${className} ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
      onClick={handleClick}
      aria-busy={isLoading}
    >
      {isLoading ? 'Loading...' : text}
    </Link>
  );
});

// Primary CTA - Large full-width
export const CtaButtonsBig = memo(function CtaButtonsBig({
  cta,
}: {
  cta: CtaGroup;
}) {
  // Memoize static content
  const { title, description, buttons } = useMemo(() => cta, [cta]);

  return (
    <div className="section-cta bg-[var(--primary--beige)] dark:bg-[var(--gray--100)]">
      <div className="container-large">
        <div className="padding-vertical padding-large">
          <div className="max-width-xlarge align-center">
            <div>
              <h2 className="text-[var(--primary--burgundy)] dark:text-[var(--primary--pink)]">
                {title}
              </h2>
              <div className="padding-bottom padding-xsmall text-align-center" />
              <div className="max-width-large">
                <div
                  className="text-size-medium"
                  dangerouslySetInnerHTML={{ __html: stripBrInLists(description) }}
                />
              </div>
            </div>
            <div className="padding-bottom padding-small" />
            <div className="button-row is-centered-buttons">
              {buttons.map((btn, i) => (
                <CtaButton key={`cta-big-${i}`} {...btn} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// Secondary CTA - Inline variant
export const CtaSecondary = memo(function CtaSecondary({
  title,
  buttonText,
  buttonHref,
  variant = 'secondary',
}: {
  title: string;
  buttonText: string;
  buttonHref: string;
  variant?: 'primary' | 'secondary';
}) {
  const { trackEvent } = useAnalytics();

  const handleClick = useCallback(() => {
    trackEvent('cta_secondary_click', {
      cta_title: title,
      cta_href: buttonHref,
    });
  }, [title, buttonHref, trackEvent]);

  return (
    <div className="section-cta-secondary">
      <div className="container-large">
        <div className="padding-vertical padding-medium">
          <div className="flex items-center justify-between gap-4">
            <h3 className="heading-small text-[var(--primary--burgundy)] dark:text-[var(--primary--pink)]">
              {title}
            </h3>
            <Link
              href={buttonHref}
              className={`button is-${variant}`}
              onClick={handleClick}
            >
              {buttonText}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
});

// Sticky CTA - Fixed at bottom
export const CtaSticky = memo(function CtaSticky({
  text,
  href,
  isVisible = true,
}: {
  text: string;
  href: string;
  isVisible?: boolean;
}) {
  const { trackEvent } = useAnalytics();

  const handleClick = useCallback(() => {
    trackEvent('cta_sticky_click', {
      cta_text: text,
      cta_href: href,
    });
  }, [text, href, trackEvent]);

  // Memoize style
  const stickyStyle = useMemo(
    () => ({
      transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
      transition: 'transform 0.3s ease-in-out',
    }),
    [isVisible]
  );

  return (
    <div
      className="cta-sticky fixed bottom-0 left-0 right-0 z-50 bg-[var(--primary--burgundy)] dark:bg-[var(--primary--pink)] shadow-lg"
      style={stickyStyle}
    >
      <div className="container-large">
        <div className="padding-vertical padding-small">
          <Link
            href={href}
            className="button is-primary w-full text-center"
            onClick={handleClick}
          >
            {text}
          </Link>
        </div>
      </div>
    </div>
  );
});

// Final CTA - End of page
export const CtaFinal = memo(function CtaFinal({
  title,
  description,
  primaryButton,
  secondaryButton,
}: {
  title: string;
  description: string;
  primaryButton: { text: string; href: string };
  secondaryButton?: { text: string; href: string };
}) {
  const [isPrimaryLoading, setIsPrimaryLoading] = useState(false);
  const { trackEvent } = useAnalytics();

  const handlePrimaryClick = useCallback(() => {
    setIsPrimaryLoading(true);
    trackEvent('cta_final_primary_click', {
      cta_title: title,
      cta_href: primaryButton.href,
    });

    // Reset loading state after navigation
    setTimeout(() => setIsPrimaryLoading(false), 2000);
  }, [title, primaryButton.href, trackEvent]);

  const handleSecondaryClick = useCallback(() => {
    if (secondaryButton) {
      trackEvent('cta_final_secondary_click', {
        cta_title: title,
        cta_href: secondaryButton.href,
      });
    }
  }, [title, secondaryButton, trackEvent]);

  return (
    <section className="section-cta-final bg-[var(--primary--beige)] dark:bg-[var(--gray--100)]">
      <div className="padding-global">
        <div className="container-large">
          <div className="padding-vertical padding-xlarge">
            <div className="max-width-large align-center text-align-center">
              <h2 className="heading-large text-[var(--primary--burgundy)] dark:text-[var(--primary--pink)]">
                {title}
              </h2>
              <div className="padding-bottom padding-medium" />
              <p className="text-size-medium">{description}</p>
              <div className="padding-bottom padding-large" />
              <div className="button-row is-centered-buttons">
                <Link
                  href={primaryButton.href}
                  className="button is-primary"
                  onClick={handlePrimaryClick}
                  aria-busy={isPrimaryLoading}
                >
                  {isPrimaryLoading ? (
                    <>
                      <span className="spinner-icon" aria-hidden="true">
                        ⏳
                      </span>
                      Loading...
                    </>
                  ) : (
                    primaryButton.text
                  )}
                </Link>
                {secondaryButton && (
                  <Link
                    href={secondaryButton.href}
                    className="button is-secondary"
                    onClick={handleSecondaryClick}
                  >
                    {secondaryButton.text}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

// Compact CTA - For cards or inline usage
export const CtaCompact = memo(function CtaCompact({
  text,
  href,
  icon,
}: {
  text: string;
  href: string;
  icon?: React.ReactNode;
}) {
  const { trackEvent } = useAnalytics();

  const handleClick = useCallback(() => {
    trackEvent('cta_compact_click', {
      cta_text: text,
      cta_href: href,
    });
  }, [text, href, trackEvent]);

  return (
    <Link
      href={href}
      className="cta-compact inline-flex items-center gap-2 text-[var(--primary--burgundy)] dark:text-[var(--primary--pink)] hover:underline"
      onClick={handleClick}
    >
      {icon && <span className="cta-icon">{icon}</span>}
      <span>{text}</span>
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="cta-arrow"
      >
        <path
          d="M1.293,10.707a1,1,0,0,0,1.414,0L7.854,5.561l2.292,2.293A.5.5,0,0,0,10.5,8a.489.489,0,0,0,.191-.038A.5.5,0,0,0,11,7.5v-6a.5.5,0,0,0-.5-.5h-6a.5.5,0,0,0-.354.854L6.439,4.146,1.293,9.293A1,1,0,0,0,1.293,10.707Z"
          fill="currentColor"
        />
      </svg>
    </Link>
  );
});

// Export all CTA variants
export default {
  CtaButtonsBig,
  CtaSecondary,
  CtaSticky,
  CtaFinal,
  CtaCompact,
};
